import { getApiBaseUrl } from '@/lib/api';
import { resolveUploadUrl } from '@/lib/assets/resolveUploadUrl';
import type { BrandingNormalized, PublicBrandingRaw, PublicBootstrapResult } from './types';
import { parsePublicLoadingConfig, setPublicUiBootstrap } from './publicUiBootstrap';
import { FALLBACK_LOADING_BLOCK } from './loadingBlockConstants';
import { resolveAndPublishLoadingBlock, readPersistedLoadingBlockConfig } from './publishLoadingBlockConfig';
import { emitAuthSessionEvent, emitBrandingRefreshed } from '@/lib/auth/sessionCoordinator';

const PUBLIC_BRANDING_CACHE_KEY = 'branding:v1';
const BRANDING_TTL_MS = 24 * 60 * 60 * 1000;

type BrandingCachePayload = {
  version: 1;
  savedAt: number;
  data: PublicBrandingRaw;
};

/**
 * Load cached branding from localStorage
 */
const loadCachedBrandingPayload = (): BrandingCachePayload | null => {
  try {
    const cached = localStorage.getItem(PUBLIC_BRANDING_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as BrandingCachePayload;
      if (!parsed || parsed.version !== 1 || !parsed.savedAt || !parsed.data) return null;
      return parsed;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[bootstrapPublicBranding] Failed to load cached branding:', error);
    }
  }
  return null;
};

const loadCachedBranding = (allowStale = false): PublicBrandingRaw | null => {
  const payload = loadCachedBrandingPayload();
  if (!payload) return null;
  if (!allowStale && Date.now() - payload.savedAt > BRANDING_TTL_MS) return null;
  return payload.data;
};

const FALLBACK_BOOTSTRAP: PublicBootstrapResult = {
  branding: {
    site_title: null,
    logo_src: null,
    favicon_src: null,
    loading_src: null,
    og_title: null,
    og_description: null,
    og_image_src: null,
    version: '0',
  },
  publicUi: {
    loading_animation_type: FALLBACK_LOADING_BLOCK.loadingType,
    loader_indicator_duration_ms: FALLBACK_LOADING_BLOCK.loadingSpeedMs,
    enable_animations: FALLBACK_LOADING_BLOCK.animationsEnabled,
  },
};

/**
 * Synchronous snapshot for BootstrapSplash (no API, no side effects).
 * Priority: branding:v1 cache (stale-ok) → window.__PUBLIC_UI_CONFIG__ → safe fallback.
 */
export const getBootstrapSplashSnapshot = (): PublicBootstrapResult => {
  const cachedRaw = loadCachedBranding(true);
  if (cachedRaw) {
    return {
      branding: normalizeBranding(cachedRaw),
      publicUi: parsePublicLoadingConfig(cachedRaw),
    };
  }

  const existingUi = typeof window !== 'undefined'
    ? (window as Window & { __PUBLIC_UI_CONFIG__?: PublicBootstrapResult['publicUi'] }).__PUBLIC_UI_CONFIG__
    : undefined;
  if (existingUi) {
    return {
      branding: normalizeBranding(null),
      publicUi: existingUi,
    };
  }

  return FALLBACK_BOOTSTRAP;
};

/**
 * Backward compat: migrate legacy caches into loading_block_config:v1 via normalizer (TS only).
 */
export function ensureLoadingBlockPublished(): void {
  if (typeof window === 'undefined') return;
  if (readPersistedLoadingBlockConfig()) return;

  const snapshot = getBootstrapSplashSnapshot();
  resolveAndPublishLoadingBlock({
    scope: 'boot',
    publicBranding: snapshot.branding,
    publicUi: snapshot.publicUi,
  });
}

/**
 * Save branding to localStorage cache
 */
const saveCachedBranding = (branding: PublicBrandingRaw): void => {
  try {
    const payload: BrandingCachePayload = {
      version: 1,
      savedAt: Date.now(),
      data: branding,
    };
    localStorage.setItem(PUBLIC_BRANDING_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[bootstrapPublicBranding] Failed to save cached branding:', error);
    }
  }
};

/**
 * Normalize raw branding data to BrandingNormalized
 * - Resolve all URLs via resolveUploadUrl()
 * - Generate version for cache-busting
 */
const normalizeBranding = (raw: PublicBrandingRaw | null): BrandingNormalized => {
  if (!raw) {
    return {
      site_title: null,
      logo_src: null,
      favicon_src: null,
      loading_src: null,
      og_title: null,
      og_description: null,
      og_image_src: null,
      version: '0', // Stable version for null branding
    };
  }

  // Resolve URLs to canonical format
  const logo_src = resolveUploadUrl(raw.logo_url);
  const favicon_src = resolveUploadUrl(raw.favicon_url);
  const loading_src = resolveUploadUrl(raw.loading_image_url);
  const og_image_src = resolveUploadUrl(raw.og_image);

  // Generate version from updated_at or use timestamp
  const version = raw.updated_at
    ? new Date(raw.updated_at).getTime().toString()
    : Date.now().toString();

  return {
    site_title: raw.site_title?.trim() || null,
    logo_src,
    favicon_src,
    loading_src,
    og_title: raw.og_title?.trim() || null,
    og_description: raw.og_description?.trim() || null,
    og_image_src,
    version,
  };
};

/**
 * Bootstrap public config (branding + loading UI subset) - BLOCKING fetch
 * Returns normalized branding + public UI loading config
 * 
 * Flow:
 * 1. Try to load from cache (instant)
 * 2. Fetch from API (blocking)
 * 3. If fetch fails/304, use cached
 * 4. Normalize, set window.__PUBLIC_UI_CONFIG__, return
 */
const finishBootstrap = (raw: PublicBrandingRaw | null): PublicBootstrapResult => {
  const publicUi = parsePublicLoadingConfig(raw);
  setPublicUiBootstrap(publicUi);
  const result = { branding: normalizeBranding(raw), publicUi };
  resolveAndPublishLoadingBlock({
    scope: 'boot',
    publicBranding: result.branding,
    publicUi: result.publicUi,
  });
  return result;
};

export const bootstrapPublicBranding = async (): Promise<PublicBootstrapResult> => {
  // Step 1: Load from cache (instant)
  const cached = loadCachedBranding();
  const cachedStaleOk = cached ?? loadCachedBranding(true);

  try {
    const API_URL = getApiBaseUrl();
    const response = await fetch(`${API_URL}/settings/public`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store', // Disable browser cache
    });

    // Step 2: Handle 304 Not Modified - use cached
    if (response.status === 304) {
      if (import.meta.env.DEV) {
        console.debug('[bootstrapPublicBranding] 304 Not Modified, using cached');
      }
      return finishBootstrap(cached);
    }

    // Step 3: Handle non-200 responses
    if (!response.ok) {
      if (response.status >= 500) {
        emitAuthSessionEvent({
          type: 'SERVER_ERROR',
          status: response.status,
          source: 'bootstrapPublicBranding:/settings/public',
          at: Date.now(),
          message: `Branding API ${response.status}`,
        });
      }
      if (import.meta.env.DEV) {
        console.warn('[bootstrapPublicBranding] Failed to load public branding:', response.status);
      }
      // Use cached if available, otherwise return stable null branding
      return finishBootstrap(cachedStaleOk);
    }

    // Step 4: Parse response
    const data = await response.json();
    if (data.success && data.data) {
      const raw = data.data as PublicBrandingRaw;
      // Save to cache
      saveCachedBranding(raw);
      if (import.meta.env.DEV) {
        console.debug('[bootstrapPublicBranding] Success, cached:', {
          site_title: raw.site_title,
          has_favicon: !!raw.favicon_url,
          has_logo: !!raw.logo_url,
        });
      }
      emitBrandingRefreshed('bootstrapPublicBranding:success');
      return finishBootstrap(raw);
    }

    // Invalid response format - use cached if available
    if (import.meta.env.DEV) {
      console.debug('[bootstrapPublicBranding] Invalid response format, using cached');
    }
    return finishBootstrap(cached);
  } catch (error) {
    emitAuthSessionEvent({
      type: 'NETWORK_ERROR',
      source: 'bootstrapPublicBranding:/settings/public',
      at: Date.now(),
      message: error instanceof Error ? error.message : 'Network error',
    });
    if (import.meta.env.DEV) {
      console.error('[bootstrapPublicBranding] Error loading public branding:', error);
    }
    // Network error - use cached if available, otherwise return stable null branding
    return finishBootstrap(cachedStaleOk);
  }
};

